// Window-based accessor for Auto Props (non-React usage)
// For React components, use AutoPropsProvider instead

import type { AutoPropsMap } from './type_map';

declare global {
  interface Window {
    __autoProps?: AutoPropsMap;
  }
}

function getAutoPropsByKey<K extends keyof AutoPropsMap>(key: K) {
  if (typeof window === 'undefined') {
    throw new Error(
      'getAutoProps called during SSR - use React context instead',
    );
  }

  const map = window.__autoProps;

  if (!map || typeof map !== 'object') {
    throw new Error(
      'AutoProps map not found. Make sure the auto_props partial is rendered.',
    );
  }

  const props = map[key];

  if (typeof props === 'undefined') {
    console.error(
      `AutoProps '${key}' not found. Make sure it has been registered with register_auto_props.`,
    );
  }

  return props;
}

/**
 * Retrieves auto props from the window object.
 * This should only be used in non-React TypeScript code.
 * For React components, use the useAutoProps hook instead.
 *
 * @param key - The key of the auto props to retrieve
 * @throws Error if called during SSR or if props not found
 *
 * @example
 * const topics = getAutoProps('UserTopics');
 */
export function getAutoProps<K extends keyof AutoPropsMap>(key: K) {
  return getAutoPropsByKey<K>(key);
}
